import {
  useState, useEffect, useCallback, forwardRef,
} from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { useWindowHeight, useHeaderHeight, useOnClickOutside } from 'Shared/util/custom-hooks';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { joinClassNames as c } from 'Shared/util/styles';
import Animation from 'Shared/components/animation';
import styles from './sidebar.css';


const Sidebar = forwardRef(({
  show,
  children,
  onClickOutside,
  animation,
  fullHeight,
  overlay,
  className,
}, ref) => {
  const [containerNode, setContainerNode] = useState(null);
  const [calculatedStyles, setCalculatedStyles] = useState({ height: 0, top: 0 });
  const windowHeight = useWindowHeight();
  const navBarHeight = useHeaderHeight();

  const handleClickOutside = (e) => {
    if (!onClickOutside) return;
    // Modals inside Sidebar should not trigger 'onClickOutside'. /* Note: There may be a better solution */
    if (e.target.closest('#base-modal')) return;

    onClickOutside(e);
  };
  useOnClickOutside(containerNode, handleClickOutside);

  const containerCallbackRef = useCallback((node) => {
    setContainerNode(node);

    if (ref && typeof ref === 'function') {
      ref(node);
    } else if (ref) {
      ref.current = node;
    }
  }, [ref]);

  useEffect(() => {
    if (!containerNode) return undefined;

    if (show) {
      disableBodyScroll(containerNode);
    } else {
      enableBodyScroll(containerNode);
    }

    return () => enableBodyScroll(containerNode);
  }, [show, containerNode]);

  useEffect(() => {
    if (fullHeight) {
      setCalculatedStyles({
        height: windowHeight,
        top: 0,
      });
    } else {
      setCalculatedStyles({
        height: (windowHeight - navBarHeight),
        top: navBarHeight,
      });
    }
  }, [windowHeight, navBarHeight, fullHeight]);

  const sidebar = (
    <div
      ref={containerCallbackRef}
      className={c(styles.container, (fullHeight && styles.fullHeight), className)}
      style={calculatedStyles}
    >
      {children}
    </div>
  );

  return createPortal((
    animation ? (
      <Animation
        show={show}
        animation="slide_left"
        overlay={overlay}
      >
        {sidebar}
      </Animation>
    ) : (show && (
      <>
        {sidebar}
        {overlay && <div className={styles.overlay} />}
      </>
    ))
  ),
  document.body);
});

Sidebar.defaultProps = {
  animation: true,
  overlay: true,
};

Sidebar.propTypes = {
  show: PropTypes.bool.isRequired,
  children: PropTypes.node,
  onClickOutside: PropTypes.func,
  animation: PropTypes.bool,
  fullHeight: PropTypes.bool,
  overlay: PropTypes.bool,
  className: PropTypes.string,
};

export default Sidebar;
